if (!customElements.get('ares-studio-values-carousel')) {
  class AresStudioValuesCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.ares-studio-values__viewport');
      this.track = this.querySelector('.ares-studio-values__track');
      this.slides = this.track ? Array.from(this.track.querySelectorAll('.ares-studio-values__slide')) : [];
      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.isPointerDown = false;
      this.pointerId = null;
      this.startX = 0;
      this.scrollStart = 0;
      this.settleTimer = null;
      this._dragBound = false;
      this._desktopClickBound = false;

      // Theme tablet-up: desktop = single row, click to scale. Mobile = drag carousel.
      this.desktopMq = window.matchMedia('(min-width: 1025px)');

      this.onViewportScroll = () => {
        if (!this.viewport || this.desktopMq.matches) return;
        if (this.isPointerDown) return;
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.viewport.classList.add('is-scrubbing');
        this.settleTimer = window.setTimeout(() => {
          this.viewport.classList.remove('is-scrubbing');
          this.updateCenterSlide();
          this.settleTimer = null;
        }, 90);
      };

      this.onViewportScrollEnd = () => {
        if (!this.viewport || this.desktopMq.matches) return;
        if (this.isPointerDown) return;
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.viewport.classList.remove('is-scrubbing');
        this.updateCenterSlide();
      };

      this._onResize = () => {
        if (this.settleTimer) clearTimeout(this.settleTimer);
        if (this.viewport) this.viewport.classList.remove('is-scrubbing');
        this.updateCenterSlide();
      };

      this._onMqChange = () => {
        this.syncPointerMode();
        requestAnimationFrame(() => {
          if (this.isDesktopNavigation()) {
            this.viewport.scrollLeft = 0;
            this.updateCenterSlide();
          } else {
            this.centerInitially();
          }
        });
      };

      this._onPointerDown = this.onPointerDown.bind(this);
      this._onPointerMove = this.onPointerMove.bind(this);
      this._onPointerUp = this.onPointerUp.bind(this);
      this._onPointerCancel = this.onPointerUp.bind(this);
      this._onKeyDown = this.onKeyDown.bind(this);
      this._onDesktopSlideClick = this.onDesktopSlideClick.bind(this);

      this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });
      this.viewport.addEventListener('scrollend', this.onViewportScrollEnd);
      window.addEventListener('resize', this._onResize);

      if (typeof this.desktopMq.addEventListener === 'function') {
        this.desktopMq.addEventListener('change', this._onMqChange);
      } else if (typeof this.desktopMq.addListener === 'function') {
        this.desktopMq.addListener(this._onMqChange);
      }

      this.viewport.addEventListener('keydown', this._onKeyDown);
      this.syncPointerMode();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.centerInitially());
      });
    }

    disconnectedCallback() {
      if (this.settleTimer) clearTimeout(this.settleTimer);

      if (typeof this.desktopMq?.removeEventListener === 'function') {
        this.desktopMq.removeEventListener('change', this._onMqChange);
      } else if (typeof this.desktopMq?.removeListener === 'function') {
        this.desktopMq.removeListener(this._onMqChange);
      }

      if (!this.viewport) return;

      this.viewport.removeEventListener('scroll', this.onViewportScroll);
      this.viewport.removeEventListener('scrollend', this.onViewportScrollEnd);
      window.removeEventListener('resize', this._onResize);
      this.detachDragListeners();
      this.detachDesktopClick();
      if (this._onKeyDown) this.viewport.removeEventListener('keydown', this._onKeyDown);
      this.viewport.classList.remove('ares-studio-values__viewport--desktop-nav');
    }

    isDesktopNavigation() {
      return this.desktopMq.matches;
    }

    /** Default “hero” card (center of the row, like the design). */
    defaultDesktopSlide() {
      const mid = Math.max(0, Math.floor((this.slides.length - 1) / 2));
      return this.slides[mid];
    }

    applyDesktopFocus(slide) {
      if (!slide || !this.slides.includes(slide)) return;
      this.slides.forEach((s) => {
        const on = s === slide;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
    }

    attachDesktopClick() {
      if (this._desktopClickBound || !this.viewport) return;
      this.viewport.addEventListener('click', this._onDesktopSlideClick);
      this._desktopClickBound = true;
    }

    detachDesktopClick() {
      if (!this._desktopClickBound || !this.viewport) return;
      this.viewport.removeEventListener('click', this._onDesktopSlideClick);
      this._desktopClickBound = false;
    }

    onDesktopSlideClick(e) {
      if (!this.isDesktopNavigation()) return;

      const ignore = 'a[href], button, input, textarea, select, label';
      if (e.target.closest(ignore)) return;

      const slide = e.target.closest('.ares-studio-values__slide');
      if (!slide || !this.slides.includes(slide)) return;

      this.applyDesktopFocus(slide);
    }

    syncPointerMode() {
      if (!this.viewport) return;

      const desktop = this.isDesktopNavigation();
      this.viewport.classList.toggle('ares-studio-values__viewport--desktop-nav', desktop);

      if (desktop) {
        this.detachDragListeners();
        this.attachDesktopClick();
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.viewport.classList.remove('is-scrubbing');
        this.isPointerDown = false;
        this.pointerId = null;
        this.viewport.scrollLeft = 0;
        this.updateCenterSlide();
      } else {
        this.detachDesktopClick();
        this.attachDragListeners();
      }
    }

    attachDragListeners() {
      if (this._dragBound || !this.viewport) return;
      this.viewport.addEventListener('pointerdown', this._onPointerDown);
      this.viewport.addEventListener('pointermove', this._onPointerMove, { passive: false });
      this.viewport.addEventListener('pointerup', this._onPointerUp);
      this.viewport.addEventListener('pointercancel', this._onPointerUp);
      this._dragBound = true;
    }

    detachDragListeners() {
      if (!this._dragBound || !this.viewport) return;
      this.viewport.removeEventListener('pointerdown', this._onPointerDown);
      this.viewport.removeEventListener('pointermove', this._onPointerMove);
      this.viewport.removeEventListener('pointerup', this._onPointerUp);
      this.viewport.removeEventListener('pointercancel', this._onPointerUp);
      try {
        this.viewport.style.scrollSnapType = '';
      } catch (_) {
        /* noop */
      }
      this._dragBound = false;
    }

    slideCenterScrollX(slide) {
      const trackLeft = this.track.offsetLeft;
      const midWithinTrack = slide.offsetLeft + slide.offsetWidth / 2;
      return trackLeft + midWithinTrack;
    }

    viewportCenterScrollX() {
      return this.viewport.scrollLeft + this.viewport.clientWidth / 2;
    }

    closestSlideToCenter() {
      const mid = this.viewportCenterScrollX();
      let best = null;
      let bestDist = Infinity;
      this.slides.forEach((slide) => {
        const d = Math.abs(this.slideCenterScrollX(slide) - mid);
        if (d < bestDist) {
          bestDist = d;
          best = slide;
        }
      });
      return best;
    }

    scrollTargetForSlide(slide) {
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const target = this.slideCenterScrollX(slide) - this.viewport.clientWidth / 2;
      return Math.max(0, Math.min(target, maxScroll));
    }

    updateCenterSlide() {
      if (this.isDesktopNavigation()) {
        let chosen = this.slides.find((s) => s.classList.contains('is-active'));
        if (!chosen) chosen = this.defaultDesktopSlide();
        this.applyDesktopFocus(chosen);
        return;
      }

      const best = this.closestSlideToCenter();
      if (!best) return;
      this.slides.forEach((slide) => {
        const active = slide === best;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    snapScrollToNearest() {
      if (this.isDesktopNavigation()) return;
      const best = this.closestSlideToCenter();
      if (!best) return;
      this.viewport.scrollLeft = this.scrollTargetForSlide(best);
      this.viewport.classList.remove('is-scrubbing');
      this.updateCenterSlide();
    }

    scrollSlideIntoCenter(slide) {
      if (!slide || !this.viewport || this.isDesktopNavigation()) return;
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.viewport.classList.remove('is-scrubbing');

      const prefersReduce = prefersReducedMotion();
      const smooth = !prefersReduce;
      try {
        this.viewport.scrollTo({
          left: this.scrollTargetForSlide(slide),
          behavior: smooth ? 'smooth' : 'auto',
        });
      } catch (_) {
        this.viewport.scrollLeft = this.scrollTargetForSlide(slide);
      }

      if (prefersReduce) {
        this.updateCenterSlide();
      } else {
        this.settleTimer = window.setTimeout(() => this.updateCenterSlide(), 420);
      }
    }

    centerInitially() {
      if (!this.viewport || this.slides.length === 0) return;
      this.viewport.classList.remove('is-scrubbing');

      if (this.isDesktopNavigation()) {
        this.viewport.scrollLeft = 0;
        this.applyDesktopFocus(this.defaultDesktopSlide());
        return;
      }

      this.viewport.scrollLeft = this.scrollTargetForSlide(this.slides[0]);
      this.updateCenterSlide();
    }

    onPointerDown(e) {
      if (this.isDesktopNavigation()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.viewport.classList.add('is-scrubbing');
      this.isPointerDown = true;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.scrollStart = this.viewport.scrollLeft;
      try {
        this.viewport.setPointerCapture(e.pointerId);
      } catch (_) {
        /* noop */
      }
      this.viewport.style.scrollSnapType = 'none';
    }

    onPointerMove(e) {
      if (!this.isPointerDown || e.pointerId !== this.pointerId) return;
      if (e.pointerType !== 'touch') {
        e.preventDefault();
      }
      const dx = e.clientX - this.startX;
      this.viewport.scrollLeft = this.scrollStart - dx;
    }

    onPointerUp(e) {
      if (!this.isPointerDown || e.pointerId !== this.pointerId) return;

      try {
        this.viewport.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* noop */
      }

      this.isPointerDown = false;
      this.pointerId = null;
      this.viewport.style.scrollSnapType = '';

      if (Math.abs(e.clientX - this.startX) > 8) {
        this.snapScrollToNearest();
      } else {
        this.viewport.classList.remove('is-scrubbing');
        this.updateCenterSlide();
      }
    }

    onKeyDown(e) {
      if (this.isDesktopNavigation()) {
        const idx = this.slides.findIndex((s) => s.classList.contains('is-active'));
        const i = idx < 0 ? Math.max(0, Math.floor((this.slides.length - 1) / 2)) : idx;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.applyDesktopFocus(this.slides[Math.min(i + 1, this.slides.length - 1)]);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.applyDesktopFocus(this.slides[Math.max(i - 1, 0)]);
        }
        return;
      }

      const idx = this.slides.findIndex((s) => s.classList.contains('is-active'));
      const i = idx < 0 ? 0 : idx;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollSlideIntoCenter(this.slides[Math.min(i + 1, this.slides.length - 1)]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollSlideIntoCenter(this.slides[Math.max(i - 1, 0)]);
      }
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  customElements.define('ares-studio-values-carousel', AresStudioValuesCarousel);
}
